import React, { use, useCallback, useEffect, useRef } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { useSetAtom } from "jotai";
import { RootStackParamList } from "../navigations/RootNavigation";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { FirebaseManagerContext } from "../contexts/FirebaseManager";
import { invitationCodeStepDone, pendingInvitationCode } from "../atoms/auth";
import { useSnackbar } from "../hooks/snackbar";
import { useClaimInvitationCode, useInvitationCodeErrorMessage } from "../hooks/invitation-code";
import Loading from "../components/Loading";

type InvitationLinkProps = StackScreenProps<RootStackParamList, "InvitationLink">;

/**
 * The link of an invitation (`app/invite/?client_name=…&invitation_code=…`), opened in the app.
 * Signed in, the invitation of the code becomes the user's and is opened. Signed out or signing up,
 * the code is kept for the signup, which asks for it prefilled.
 */
const InvitationLink = ({ navigation, route }: InvitationLinkProps) => {
    const { invitation_code: invitationCode, client_name: clientName } = route.params ?? {};
    const { status, user } = use(AuthManagerContext);
    const { user: firebaseUser } = use(FirebaseManagerContext);
    const setPendingInvitationCode = useSetAtom(pendingInvitationCode);
    const setInvitationCodeStepDone = useSetAtom(invitationCodeStepDone);
    const { showSnackbar } = useSnackbar();
    const errorMessageFor = useInvitationCodeErrorMessage();
    const claimInvitationCode = useClaimInvitationCode();

    // Back where the user was, else to the first screen of the app (Home once signed up).
    const leave = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
            return;
        }
        const { routeNames } = navigation.getState();
        const landing = (
            routeNames.includes("Home") ? "Home" : routeNames[0]
        ) as keyof RootStackParamList;
        navigation.reset({ index: 0, routes: [{ name: landing }] });
    }, [navigation]);

    // Where the link leads, once the auth status is known.
    const follow = useCallback(() => {
        if (status === AuthStatus.In && user) {
            if (!invitationCode) {
                leave();
                return;
            }
            void claimInvitationCode(invitationCode).then((result) => {
                if (result.claimed) {
                    navigation.replace("Invitation", { invitationId: result.invitationId });
                } else {
                    showSnackbar({ text: errorMessageFor(result.messageKey), mode: "error" });
                    leave();
                }
            });
            return;
        }

        if (invitationCode) {
            // No Lift user yet: the signup asks for the code (prefilled with this one), and gives
            // it with the profile it creates.
            setPendingInvitationCode(invitationCode);
            setInvitationCodeStepDone(false);
        }
        if (firebaseUser) {
            // Signing up (phone number verified, no account yet): straight back to the role
            // selection, which asks for the code next.
            navigation.reset({
                index: 0,
                routes: [
                    {
                        name: "SignupSigninStack",
                        params: { initialRouteName: "SelectRole", isSignup: true },
                    },
                ],
            });
        } else {
            navigation.reset({
                index: 0,
                routes: [{ name: "Welcome", params: { invited_by: clientName } }],
            });
        }
    }, [
        claimInvitationCode,
        clientName,
        errorMessageFor,
        firebaseUser,
        invitationCode,
        leave,
        navigation,
        setInvitationCodeStepDone,
        setPendingInvitationCode,
        showSnackbar,
        status,
        user,
    ]);

    const handled = useRef(false);
    useEffect(() => {
        if (status === AuthStatus.Loading || handled.current) {
            return;
        }
        // Not from the effect itself: React Navigation applies the state a render computed when
        // that render commits, and React can run this effect just before, when a link opens the
        // app. A navigation made here was then undone (signed out, the link stayed on its spinner).
        const timer = setTimeout(() => {
            handled.current = true;
            follow();
        });
        return () => clearTimeout(timer);
    }, [follow, status]);

    return <Loading style={{ flex: 1 }} />;
};

export default InvitationLink;
